import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../services/storage.service';
import { StorageEnum } from '../enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../services/util.service';
import { isPlatformBrowser } from '@angular/common';
import { createDeepLinkPayload } from '../models/deep-link-payload.model';
import { Nav } from '../enums/nav.enum';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
  isBrowser = false;
  private readonly closedStatusFlags = ['60', '70'];

  constructor(
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly utils: UtilService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) readonly platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  private getPrimaryStatusFlag(loginResponse: any): string | null {
    return loginResponse?.Accounts?.[0]?.ServiceAddresses?.[0]?.SaStatusFlag ?? null;
  }

  private isSeasonalAccount(loginResponse: any): boolean {
    return loginResponse?.Accounts?.[0]?.Seasonal === true;
  }

  private isClosedPath(url: string): boolean {
    return url.startsWith(Nav.Closed);
  }

  private isReconnectPath(url: string): boolean {
    return url.startsWith(Nav.ReconnectService);
  }

  private isSeasonalPath(url: string): boolean {
    return url.startsWith(Nav.Seasonal);
  }

  private isAdvantagePath(url: string): boolean {
    return url.startsWith(Nav.Advantage);
  }

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    if (this.isBrowser) {
      const loginResponse = this.storage.getSession(StorageEnum.LoginResponse);
      const hasMultiAcctResponse = this.storage.getSession(StorageEnum.MultiAccountResponse);

      if (this.utils.isNullOrUndefined(loginResponse) && this.utils.isNullOrUndefined(hasMultiAcctResponse)) {
        if (this.isAdvantagePath(state.url)) {
          return true;
        }

        const currentUrl = state.url;
        if (!this.utils.isNullOrUndefined(currentUrl) && currentUrl !== '/login' && currentUrl !== '/') {
          this.storage.setSession(StorageEnum.DeepLink, createDeepLinkPayload(currentUrl, 'auth-guard'));
          this.storage.hasDeepLinkRedirect.set(true);
        }
        this.utils.hasLoggedIn.set(false);
        this.storage.removeSession(StorageEnum.ClosedRouteVisited);
        this.storage.removeSession(StorageEnum.SeasonalRouteVisited);
        return this.router.createUrlTree(['/login']);
      } else {
        const statusFlag = this.getPrimaryStatusFlag(loginResponse);
        const hasClosedStatus = statusFlag !== null && this.closedStatusFlags.includes(statusFlag);
        const hasSeasonalStatus = this.isSeasonalAccount(loginResponse);
        const isClosedPath = this.isClosedPath(state.url);
        const isReconnectPath = this.isReconnectPath(state.url);
        const isSeasonalPath = this.isSeasonalPath(state.url);
        const hasVisitedClosed = this.storage.getSession(StorageEnum.ClosedRouteVisited) === true;
        const hasVisitedSeasonal = this.storage.getSession(StorageEnum.SeasonalRouteVisited) === true;

        if (hasSeasonalStatus && isSeasonalPath) {
          this.storage.setSession(StorageEnum.SeasonalRouteVisited, true);
          return true;
        }

        if (hasSeasonalStatus && hasVisitedSeasonal && !isSeasonalPath && !isReconnectPath) {
          return this.router.createUrlTree([Nav.Seasonal]);
        }

        if (!hasSeasonalStatus && hasVisitedSeasonal) {
          this.storage.removeSession(StorageEnum.SeasonalRouteVisited);
        }

        if (hasClosedStatus && isClosedPath) {
          this.storage.setSession(StorageEnum.ClosedRouteVisited, true);
          return true;
        }

        if (hasClosedStatus && hasVisitedClosed && !isClosedPath && !isReconnectPath) {
          return this.router.createUrlTree([Nav.Closed]);
        }

        if (!hasClosedStatus && hasVisitedClosed) {
          this.storage.removeSession(StorageEnum.ClosedRouteVisited);
        }

        return true;
      }
    } else {
      return true;
    }
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.canActivate(route, state);
  }
}
